import { init, getCurrentScope, browserTracingIntegration, rewriteFramesIntegration, } from '@sentry/browser';
import { init as initCapacitor } from '@sentry/capacitor';
import { UAParser } from 'ua-parser-js';
import { checkIfFlagIsToggled, DebugLocalStorageFlags } from '@famly/vet_ts-utils_local-storage-config';
import { redactor } from '@famly/vet_ts-utils_logging';
import { getGraphQLErrors } from '@famly/stat_apollo-query';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { addReplayIntegration } from '@famly/vet_services_sentry';
import { findCrewFromPath, findRouteTemplateFromUncleanUrl } from 'web-app/react/routes/helpers';
import { APIErrorCode } from '@famly/vet_api-error-codes';
import { RestClientNetworkError } from 'web-app/api/clients/rest-client-network-error';
import { isEligibleForReplays } from './sentry-helpers';
const FORCED_ROUTES_SAMPLE_RATE = 1;
// ChunkLoadErrors are dominated by transient causes (network blips, a deploy landing
// mid-session) that the webpack chunk-load retry in webpack-app-entry.tsx recovers from.
// We previously dropped 100% of them, leaving us blind to genuine regressions in chunk
// delivery. We now keep a small sample so dashboards retain signal without Sentry being
// flooded — multiply the sampled count by 1 / this rate to estimate the true volume.
const CHUNK_LOAD_ERROR_SAMPLE_RATE = 0.01;
const routesWithForcedTracking = [
    '/account/institution/:institutionId/revenueReport',
    '/account/institution/:institutionId/billPayersReport',
    '/account/institution/:institutionId/publicFundingReport',
    '/account/institution/:institutionId/batchInvoice',
];
const getSentryConfig = (ENV) => {
    const { staging, production, target } = ENV.sentry;
    switch (target) {
        case 'staging':
            return staging;
        case 'production':
            return production;
        default:
            return undefined;
    }
};
// Different browsers use different wording for network errors, so we need to check for all of them
// Produced by iterating through Sentry issues and their raw JSON payloads.
// If `value` is null, any error of that type is ignored.
// Matched by prefix, because `@sentry/browser` v10's fetch instrumentation enriches the native
// TypeError with the request hostname, e.g. `Load failed` becomes `Load failed (api.famly.de)`.
export const badNetworkErrors = [
    // Chrome (e.g. on Android) uses this wording for failed requests
    { type: 'TypeError', value: 'Failed to fetch' },
    // Safari uses this wording for failed requests
    { type: 'TypeError', value: 'Load failed' },
    { type: 'Error', value: 'TypeError: Load failed' },
    // Safari uses this wording for cancelled requests
    { type: 'TypeError', value: 'cancelled' },
    { type: 'Error', value: 'TypeError: cancelled' },
    // Safari uses this wording for aborted operations
    { type: 'Error', value: 'AbortError: The operation was aborted.' },
    // Represents a timeout in Chrome
    { type: 'Error', value: 'AbortError: The user aborted a request.' },
    // Represents a timeout in Safari
    { type: 'AbortError', value: 'Fetch is aborted.' },
    { type: 'AbortError', value: 'Fetch is aborted' },
    // Thrown by Apollo's RetryLink/ErrorLink when a request is cancelled mid-flight
    // (e.g. user navigates away before a query resolves). The abort is intentional — not a bug.
    { type: 'AbortError', value: 'signal is aborted without reason' },
    // Network timeouts
    { type: 'TypeError', value: 'The request timed out.' },
    { type: 'Error', value: 'Network error: The request timed out.' },
    { type: 'Error', value: 'Fetch request aborted due to timeout' },
    // No connection
    { type: 'TypeError', value: 'The Internet connection appears to be offline.' },
    { type: 'TypeError', value: 'The network connection was lost.' },
    // iOS surfaces its native URL-loading errors (NSURLError) to JavaScript as a TypeError
    // whose message is the OS' localised error description. The English wordings above only
    // catch English-locale devices, so the same connectivity failures leak from our large
    // German and Danish user bases. These are the localised equivalents seen in Sentry:
    // DNS lookup failure (NSURLErrorCannotFindHost) — the English wording isn't covered above either.
    { type: 'TypeError', value: 'A server with the specified hostname could not be found.' },
    // "The Internet connection appears to be offline." in German
    { type: 'TypeError', value: 'Es besteht anscheinend keine Verbindung zum Internet.' },
    // "cancelled" in German
    { type: 'TypeError', value: 'Abgebrochen' },
    // "cancelled" in Danish
    { type: 'TypeError', value: 'annulleret' },
];
export const isBadNetworkError = (incomingError) => {
    return badNetworkErrors.some(badNetworkError => {
        var _a, _b;
        if ((incomingError === null || incomingError === void 0 ? void 0 : incomingError.type) !== badNetworkError.type) {
            return false;
        }
        if (badNetworkError.value === null) {
            return true;
        }
        return (_b = (_a = incomingError === null || incomingError === void 0 ? void 0 : incomingError.value) === null || _a === void 0 ? void 0 : _a.startsWith(badNetworkError.value)) !== null && _b !== void 0 ? _b : false;
    });
};
/**
 * Whether the given exception is a webpack chunk-load failure.
 *
 * @param incomingError the first exception attached to a Sentry event, if any
 * @returns `true` when the exception type is `ChunkLoadError`
 */
export const isChunkLoadError = (incomingError) => (incomingError === null || incomingError === void 0 ? void 0 : incomingError.type) === 'ChunkLoadError';
/**
 * Whether the given exception is the unactionable `InvalidStateError` raised by the
 * iOS Capacitor WebView's native media layer.
 *
 * iOS WKWebViews surface an unhandled, unsymbolicated `DOMException`
 * (`InvalidStateError: The object is in an invalid state.`, code 11) from the built-in
 * video player — e.g. while the native controls are scrubbed or the volume is changed on
 * the feed. It reaches Sentry via the global `onerror` handler as a single anonymous frame,
 * so there is nothing in our code to prevent at the throw site.
 *
 * The match is intentionally narrow (exact value + the global-`onerror` mechanism) and is
 * only applied on mobile builds by the caller, so genuine web-side `InvalidStateError`s
 * (IndexedDB, WebSocket, etc.) are still reported.
 *
 * @param incomingError the first exception attached to a Sentry event, if any
 * @returns `true` when the exception is the iOS native-media `InvalidStateError`
 */
export const isIosNativeMediaInvalidStateError = (incomingError) => {
    var _a;
    return (incomingError === null || incomingError === void 0 ? void 0 : incomingError.type) === 'Error' &&
        (incomingError === null || incomingError === void 0 ? void 0 : incomingError.value) === 'InvalidStateError: The object is in an invalid state.' &&
        ((_a = incomingError === null || incomingError === void 0 ? void 0 : incomingError.mechanism) === null || _a === void 0 ? void 0 : _a.type) === 'auto.browser.global_handlers.onerror';
};
/**
 * Whether the backend hinted, via the `log` extension on every GraphQL error it
 * answered with, that this failure is not worth recording.
 *
 * The emptiness check carries the weight: an error with no GraphQL errors carries
 * no hint either, and `every` on an empty list is vacuously true — without it every
 * non-GraphQL error would be dropped before it reached Sentry.
 */
export const isUnloggableGraphQLError = (error) => {
    const graphQLErrors = getGraphQLErrors(error);
    return graphQLErrors.length > 0 && graphQLErrors.every(graphQLError => { var _a; return ((_a = graphQLError.extensions) === null || _a === void 0 ? void 0 : _a.log) === false; });
};
/**
 * Decides whether a sampled event should be kept for delivery to Sentry.
 *
 * @param sampleRate fraction of events to keep, in the range [0, 1]
 * @param roll a value in [0, 1), typically `Math.random()` — injected so the decision is testable
 * @returns `true` when the event falls within the sample and should be sent
 */
export const shouldKeepSampledEvent = (sampleRate, roll) => roll < sampleRate;
const whenPageLoaded = () => new Promise(resolve => {
    if (document.readyState === 'complete') {
        resolve();
    }
    else {
        window.addEventListener('load', () => resolve(), { once: true });
    }
});
const setupSentry = (ENV, enableGlobalErrorCatching) => {
    const config = getSentryConfig(ENV);
    if (!config) {
        return;
    }
    if (config.development) {
        if (config.debug) {
            // eslint-disable-next-line no-console
            console.log('`sentry` is configured for development mode.');
        }
        return;
    }
    const { dsn, environment, debug = true, allowUrls = [], denyUrls = [], sentryOptions = {} } = config;
    const isMobileBuild = ENV.APP.platform === 'ios' || ENV.APP.platform === 'android';
    // UAParser doesn't detect desktops directly, but we can infer it from the device type being undefined
    // More info here: https://github.com/faisalman/ua-parser-js/issues/182
    const uaParser = new UAParser();
    const isDesktop = uaParser.getDevice().type === undefined;
    const osFromUserAgent = uaParser.getOS();
    const osInfo = osFromUserAgent.name && osFromUserAgent.version
        ? { name: osFromUserAgent.name, version: osFromUserAgent.version }
        : null;
    let replaysOnErrorSampleRate = 0;
    let replaysSessionSampleRate = 0;
    if (isMobileBuild) {
        // Both rates stay at 0, as the native apps have no replay integration
    }
    else if (isDesktop) {
        replaysOnErrorSampleRate = 1;
        replaysSessionSampleRate = 0.0001;
    }
    else if (osInfo && isEligibleForReplays(osInfo)) {
        // Mobile browsers only record replays on the newest OS versions, see `isEligibleForReplays`
        replaysOnErrorSampleRate = 1;
        replaysSessionSampleRate = 0.0001;
    }
    else if (checkIfFlagIsToggled('SENTRY_ENABLE_REPLAY')) {
        replaysOnErrorSampleRate = 1;
        replaysSessionSampleRate = 1;
    }
    // Requests going to these URLs will get trace headers attached automatically by the Sentry SDK. See more info at:
    // https://docs.sentry.io/platforms/javascript/tracing/instrumentation/automatic-instrumentation/#tracepropagationtargets
    const backendTracePropagationTargets = ENV.APP.apiConfigs
        .flatMap(config => [config.api, config.graphql])
        .map(target => 
    // The Sentry SDK checks the targets against the request URL, so we need to make sure it's a full URL that includes the origin.
    // Otherwise, things like `/api` could match a lot of other external URLs.
    // E.g. the URL `https://api.external-service.com` would match on the `/api` part.
    // If the target starts with http(s), we assume it's a full URL and don't need to add the browser's current origin.
    //
    // When making a "normal" web build, the origin is _not_ included in ENV.APP.apiConfigs
    // as we then use the browser's current origin when making requests to the backend APIs.
    // However, for mobile app builds, the origin _is_ included in ENV.APP.apiConfigs as the browser's origin in that case
    // is `capacitor://localhost`. The same is the case when we want to e.g. target the production backend from a local build.
    target.startsWith('http') ? target : `${window.location.origin}${target}`);
    const tracePropagationTargets = ['localhost', ...backendTracePropagationTargets];
    try {
        // Keeping existing config values for allowUrls, for compatibility.
        const sentryConfig = {
            environment,
            allowUrls,
            denyUrls,
            debug,
            release: ENV.APP.sentryVersion,
            dist: isMobileBuild ? ENV.APP.buildNumber : undefined,
            attachStacktrace: true,
            tracePropagationTargets,
            integrations: [
                browserTracingIntegration({
                    shouldCreateSpanForRequest,
                    beforeStartSpan: options => {
                        const routeUrl = window.location.href;
                        const routeTemplate = findRouteTemplateFromUncleanUrl(routeUrl);
                        const spanName = routeTemplate || routeUrl;
                        if (options.op === 'navigation') {
                            return {
                                ...options,
                                op: 'Navigation',
                                name: spanName,
                            };
                        }
                        if (options.op === 'pageload') {
                            return {
                                ...options,
                                op: 'Pageload',
                                name: spanName,
                            };
                        }
                        return options;
                    },
                }),
            ],
            ...sentryOptions,
            dsn,
            replaysOnErrorSampleRate,
            replaysSessionSampleRate,
            /**
             * While you're testing, enable TRACE_ALL_TRANSACTIONS flag, as that ensures that every transaction will be sent to Sentry.
             * Once testing is complete, we recommend lowering this value in production or to add the route to the forced tracking list.
             */
            tracesSampler: ({ name }) => {
                if (routesWithForcedTracking.includes(name)) {
                    return FORCED_ROUTES_SAMPLE_RATE;
                }
                return checkIfFlagIsToggled(DebugLocalStorageFlags.TRACE_ALL_TRANSACTIONS.flag) ? 1 : 0.01;
            },
            beforeSend: (event, hint) => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
                // Skipping event filtering when the in diagnostics mode
                if (checkIfFlagIsToggled(DebugLocalStorageFlags.SENTRY_DIAGNOSTIC_MODE.flag)) {
                    // Adding a flag to easily identify events that are sent in diagnostic mode
                    if (event.tags) {
                        event.tags.diagnosticMode = true;
                    }
                    else {
                        event.tags = {
                            diagnosticMode: true,
                        };
                    }
                    return event;
                }
                // Emulators (probably Google Play pre-launch testing of every upload) flood Sentry with errors no user
                // sees. The native SDK sets `simulator` from the device; diagnostic mode above still lets them through.
                if (((_b = (_a = event.contexts) === null || _a === void 0 ? void 0 : _a.device) === null || _b === void 0 ? void 0 : _b.simulator) === true) {
                    return null;
                }
                // Attempt to map the route to a crew (to assist with auto assigning issues to the relevant crew).
                // Both tags carry the crew name: ownership rules are migrating from `crew` to `team`, and each
                // rule set has to keep matching until that finishes.
                if ((_c = event.request) === null || _c === void 0 ? void 0 : _c.url) {
                    const routeTemplate = findRouteTemplateFromUncleanUrl(event.request.url);
                    if (routeTemplate) {
                        const crew = findCrewFromPath(routeTemplate);
                        if (crew) {
                            event.tags = {
                                ...event.tags,
                                route: routeTemplate,
                                crew,
                                team: crew,
                            };
                        }
                    }
                }
                const firstException = (_e = (_d = event === null || event === void 0 ? void 0 : event.exception) === null || _d === void 0 ? void 0 : _d.values) === null || _e === void 0 ? void 0 : _e[0];
                const stackTrace = (_g = (_f = firstException === null || firstException === void 0 ? void 0 : firstException.stacktrace) === null || _f === void 0 ? void 0 : _f.frames) !== null && _g !== void 0 ? _g : [];
                // Issues caused by extensions in Safari
                // In newer versions of the Sentry service I believe these are excluded automatically.
                // https://github.com/getsentry/sentry-javascript/discussions/5875
                if (stackTrace.some(frame => { var _a; return (_a = frame.filename) === null || _a === void 0 ? void 0 : _a.startsWith('webkit-masked-url://hidden/'); })) {
                    return null;
                }
                // Used for e.g. filtering Sentry events
                // https://docs.sentry.io/platforms/javascript/guides/electron/configuration/options/#before-send
                if ((firstException === null || firstException === void 0 ? void 0 : firstException.value) === 'TransitionAborted') {
                    return null;
                }
                // If the error is a network error we want to add the response to the event
                if (hint.originalException instanceof RestClientNetworkError) {
                    event.extra = { ...((_h = event.extra) !== null && _h !== void 0 ? _h : {}), response: hint.originalException.response };
                    // Filter out errors from the /client-events endpoint (analytics/famlytics)
                    // These are non-critical and the service can sometimes be unavailable
                    // Only ignore when the status code is >= 500
                    if (((_j = hint.originalException.message) === null || _j === void 0 ? void 0 : _j.includes('/client-events')) &&
                        ((_l = (_k = hint === null || hint === void 0 ? void 0 : hint.originalException) === null || _k === void 0 ? void 0 : _k.response) === null || _l === void 0 ? void 0 : _l.status) >= 500) {
                        return null;
                    }
                }
                if (event === null || event === void 0 ? void 0 : event.extra) {
                    // We see that some events are sent to Sentry with a __serialized__ extra property.
                    // This happens when a non-Error object is passed to Sentry.captureException().
                    // Documented here: https://docs.sentry.io/platforms/javascript/troubleshooting/#events-with-non-error-exception
                    const serializedExtras = event.extra.__serialized__;
                    // We don't want to send events to Sentry related to expiring sessions
                    if (((_m = serializedExtras === null || serializedExtras === void 0 ? void 0 : serializedExtras.responseData) === null || _m === void 0 ? void 0 : _m.errorCode) === APIErrorCode.SESSION_EXPIRED) {
                        return null;
                    }
                }
                // We're not interested in logging network connectivity errors to Sentry
                if (isBadNetworkError(firstException)) {
                    return null;
                }
                // ChunkLoadErrors are high-volume and mostly transient, so we keep only a sample.
                // Tag the survivors with the rate they were sampled at so dashboards can extrapolate
                // the true volume (count / rate) and tell sampled noise apart from a real regression.
                if (isChunkLoadError(firstException)) {
                    if (!shouldKeepSampledEvent(CHUNK_LOAD_ERROR_SAMPLE_RATE, Math.random())) {
                        return null;
                    }
                    event.tags = { ...event.tags, sampled: 'chunk-load-error', sampleRate: CHUNK_LOAD_ERROR_SAMPLE_RATE };
                }
                // Stripe's SDK throws IntegrationErrors when mobile webviews suspend or discard
                // the iframe hosting the PaymentElement (e.g. app backgrounded, memory pressure).
                // These are internal Stripe errors we cannot prevent in our code.
                const isStripeError = (firstException === null || firstException === void 0 ? void 0 : firstException.type) === 'IntegrationError' &&
                    stackTrace.some(frame => { var _a; return (_a = frame.filename) === null || _a === void 0 ? void 0 : _a.includes('js.stripe.com'); }) &&
                    (((_o = firstException === null || firstException === void 0 ? void 0 : firstException.value) === null || _o === void 0 ? void 0 : _o.includes('We could not retrieve data from the specified Element')) ||
                        ((_p = firstException === null || firstException === void 0 ? void 0 : firstException.value) === null || _p === void 0 ? void 0 : _p.includes('This Element has already been destroyed')));
                if (isStripeError) {
                    return null;
                }
                // iOS Capacitor WebViews raise an unhandled, unsymbolicated InvalidStateError from
                // the native video player (see isIosNativeMediaInvalidStateError). It's unactionable
                // platform noise, so drop it on mobile builds only — web-side InvalidStateErrors
                // (IndexedDB, WebSocket, etc.) are still worth reporting.
                if (isMobileBuild && isIosNativeMediaInvalidStateError(firstException)) {
                    return null;
                }
                return event;
            },
            // It's import that we ignore errors _before_ submitting events to our Sentry servers.
            // Not only does this lower load, it also avoids capturing Replays that we're not interested in.
            ignoreErrors: [
                'The OS most likely terminated your app because it overused RAM',
                'Received a memory warning from applicationDidReceiveMemoryWarning',
                // We're seeing a lot of these in Sentry after introducing ResizeObserver in Famly Home (https://github.com/famly/app/pull/20149).
                // They're flooding our Sentry infrastructure, and they're seemingly not causing any issues for end users,
                // so we're ignoring them for now.
                'ResizeObserver loop completed with undelivered notifications',
                // Error raised by Quill that's not user-facing.
                'a.domNode.getBoundingClientRect is not a function',
                // Random network errors:
                'Network error when calling',
                'undefined - /*',
                // Sometimes `famlygathering` can't keep up with all events. We're okay with not logging this.
                'Service Unavailable - /client-events',
                '- /client-events',
                // These are raised somewhere outside of our code in Capacitor builds for iOS, and do not seem to affect users.
                "Can't find variable: EmptyRanges",
                // The iOS Share plugin rejects with "Share canceled" when the user dismisses the native
                // share sheet without picking a target. That's an expected user action, not an error.
                'Share canceled',
                // These seem to be caused by extensions (Google Translate):
                "undefined is not an object (evaluating 'a.J')",
                "undefined is not an object (evaluating 'a.H')",
                // These seem to be part of the native video support in iOS:
                "undefined is not an object (evaluating 'media.currentTime')",
                "undefined is not an object (evaluating 'media.duration')",
                "undefined is not an object (evaluating 'media.webkitPresentationMode')",
                "undefined is not an object (evaluating 'media.webkitSupportsPresentationMode')",
                "undefined is not an object (evaluating 'this.media.readyState')",
                "undefined is not an object (evaluating 'this.media.videoTracks')",
                "undefined is not an object (evaluating 'this.mediaController.media.audioTracks')",
                "undefined is not an object (evaluating 'this.mediaController.media.webkitCurrentPlaybackTargetIsWireless')",
                // Third-party extensions are raising this error in Safari (and Mobile Safari) 18.x and 26.x (last spotted version was 26.1 in October 2025)
                'Invalid call to runtime.sendMessage()',
                /failed loading locales.*\.json: Load failed/i,
                /failed parsing locales.*\.json to json/i,
            ],
        };
        const beforeBreadcrumb = (breadcrumb, hint) => {
            if (breadcrumb.category === 'console' && breadcrumb.level === 'warning') {
                // Filter out console warnings as they are typically not useful
                return null;
            }
            // Change the breadcrumb if it's a GraphQL request so we can see which operation was called
            breadcrumb = transformGraphQLBreadcrumb(breadcrumb, hint);
            return breadcrumb;
        };
        if (isMobileBuild) {
            initCapacitor({
                dsn: sentryConfig.dsn,
                release: sentryConfig.release,
                debug: sentryConfig.debug,
                allowUrls: sentryConfig.allowUrls,
                // We don't set denyUrls here, since there's no URLs we want to deny on mobile builds
                beforeSend: sentryConfig.beforeSend,
                ignoreErrors: sentryConfig.ignoreErrors,
                attachStacktrace: sentryConfig.attachStacktrace,
                environment: sentryConfig.environment,
                integrations: [
                    // This integration rewrites stack frames, which we need specifically for Android,
                    // where stack frames start with `/`, which Sentry doesn't like.
                    // The default integration prefixes stack frames with `app://`, which works for us.
                    rewriteFramesIntegration(),
                ],
                beforeBreadcrumb,
                // Mobile sentry specyfic options
                enableAppHangTracking: true,
                // Sentry's iOS SDK expects this threshold in seconds, not milliseconds
                appHangTimeoutInterval: 10,
            }, init);
        }
        else {
            init({
                ...sentryConfig,
                beforeBreadcrumb,
            });
        }
    }
    catch (e) {
        console.warn(`Error during 'sentry' initialization: ${e}`); // tslint:disable-line no-console
        return;
    }
    // `@sentry/capacitor` does not support Session Replay, so the native apps never record replays.
    // Elsewhere Replay waits for the page to finish loading, so it does not compete with the app's
    // own chunks. Added after `init`, it takes its sample rates from the options above.
    if (!isMobileBuild) {
        addReplayIntegration(() => whenPageLoaded()
            .then(() => import('./create-replay-integration'))
            .then(({ createReplayIntegration }) => createReplayIntegration()));
    }
    if (enableGlobalErrorCatching) {
        enableGlobalErrorCatching();
    }
    configureSentryScope(scope => {
        scope.addEventProcessor((event, hint) => {
            var _a, _b;
            // This attempts to filter out errors that the backend hints that we shouldn't log.
            if (((_b = (_a = hint === null || hint === void 0 ? void 0 : hint.originalException) === null || _a === void 0 ? void 0 : _a.response) === null || _b === void 0 ? void 0 : _b.log) === false) {
                return null;
            }
            // Catches the errors that arise from not manually catching a failing GraphQL mutation.
            if (isUnloggableGraphQLError(hint === null || hint === void 0 ? void 0 : hint.originalException)) {
                return null;
            }
            return event;
        });
    });
};
// Function to update the breadcrumb if it's determined as a GraphQL request
const transformGraphQLBreadcrumb = (breadcrumb, hint) => {
    // Type checks
    if (!isGraphQLRequest(breadcrumb) || !hasValue(hint) || !hasValue(breadcrumb.data)) {
        return breadcrumb;
    }
    // Attempt to parse operation name
    const { operationName, type, variables } = getGraphQLDataFromHint(hint);
    if (!hasValue(operationName)) {
        return breadcrumb;
    }
    // Finally transform breadcrumb
    return {
        ...breadcrumb,
        type: 'query',
        category: 'GraphQL',
        message: operationName,
        data: {
            ...breadcrumb.data,
            type,
            variables: variables && redactor(variables),
        },
    };
};
// Function to determine if breadcrumb is a GraphQL request
const isGraphQLRequest = (breadcrumb) => {
    var _a, _b, _c;
    return (_c = (breadcrumb.category === 'fetch' && ((_b = (_a = breadcrumb.data) === null || _a === void 0 ? void 0 : _a.url) === null || _b === void 0 ? void 0 : _b.includes('graphql')))) !== null && _c !== void 0 ? _c : false;
};
// Attempts to get the operation name from the breadcrumb hint
const getGraphQLDataFromHint = (hint) => {
    // Type check
    if (!hasValue(hint.input)) {
        return {};
    }
    const [, requestData] = hint.input;
    // More type checking
    if (!hasValue(requestData) || !hasValue(requestData.body) || typeof requestData.body !== 'string') {
        return {};
    }
    // Attempt to parse body and read operation name from query/mutation
    try {
        const requestBody = JSON.parse(requestData.body);
        if (!hasValue(requestBody.operationName)) {
            return {};
        }
        return {
            operationName: requestBody.operationName,
            variables: requestBody.variables,
            type: requestBody.query.startsWith('query') ? 'query' : 'mutation',
        };
    }
    catch (_a) {
        // Return {} if JSON.parse fails
        return {};
    }
};
export const configureSentryScope = (callback) => {
    callback(getCurrentScope());
};
/**
 * Filtering function used to determine whether a span should be
 * created for a specific HTTP request. See more in Sentry's documentation:
 *
 * https://docs.sentry.io/platforms/javascript/guides/nextjs/tracing/instrumentation/automatic-instrumentation/#shouldcreatespanforrequest
 *
 * @param url The URL of the HTTP request
 * @returns `true` if a span should be created, `false` otherwise
 */
export const shouldCreateSpanForRequest = (url) => {
    const isUrlBlockListed = denyListedEndpoints.some(blockListedUrl => url.includes(blockListedUrl));
    return !isUrlBlockListed;
};
// We don't want to include health checks, notification updates or Famlytics events in our traces
// as they are called on an interval and will just cause noise.
const denyListedEndpoints = ['graphql?KeepAlive', 'graphql?NotificationUpdates', 'client-events'];
export default setupSentry;
